import { Fragment, useContext, useEffect, useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CommonButton from "../../common/CommonButton";
import ModalCloseButton from "../../common/ModalCloseButton";
import ReactSelector from "../../common/CommonReactSelector";
import { components } from "react-select";
import {
  getAllGeoCode,
  UpdateAssignrule,
} from "../../../service/Configuration";
import AppContext from "../../../context/AppContext";
import { toast } from "../../../utils/toast";
import { fetchFieldAgentMapProject } from "../../../service/Task";
import translations from "../../common/Translations";
import AssigneePicker, { emptySarRule, getRuleAssignScope } from "../../common/AssigneePicker";
import {
  currentUserCanUseGroups,
  loadGroupsForDepartment,
  normalizeGroups,
} from "../../task/GroupAssignParts";

const Placeholder = (props) => <components.Placeholder {...props} />;

// Same extraction as NewRuleModal.js -- surfaces the backend's validation
// message (workflow access, duplicate user+workflow rule, etc.).
const getBackendMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.header?.message ??
  error?.response?.data?.message;

export default function UpdateRuleModal({
  setUpdateOpen,
  updateOpen,
  updateRule,
  workflowOptions = [],
  onSaved,
}) {
  const { projectId, langMode, selectedOrgId } = useContext(AppContext);
  const [rules, setRules] = useState([emptySarRule()]);
  const [agentList, setAgentList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [GeoCodes, setGeoCodes] = useState([]);
  const [loadingGeoCodes, setLoadingGeoCodes] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const canUseGroups = currentUserCanUseGroups();

  const groupOptions = (groups || []).map((g) => ({
    value: g.groupId,
    label: g.groupName,
  }));

  useEffect(() => {
    const loadGroups = async () => {
      if (!canUseGroups) {
        setGroups([]);
        return;
      }
      const raw = await loadGroupsForDepartment();
      setGroups(normalizeGroups(raw));
    };
    loadGroups();
  }, [canUseGroups, selectedOrgId]);

  useEffect(() => {
    if (!updateRule) return;

    const fetchInitialData = async () => {
      try {
        setLoadingAgents(true);
        setLoadingGeoCodes(true);

        const [geo, agentsResponse] = await Promise.all([
          getAllGeoCode(projectId),
          fetchFieldAgentMapProject(projectId),
        ]);

        setGeoCodes(geo || []);

        if (agentsResponse?.body?.value) {
          const transformedAgentList = agentsResponse.body.value.map(agent => ({
            userId: agent.userId,
            userName: agent.userName,
          }));
          setAgentList(transformedAgentList);
        } else {
          setAgentList([]);
        }

        const assignScope =
          updateRule.groupId != null && updateRule.groupId !== "" ? "groups" : "users";
        setRules([{
          workflowId: updateRule.workflowId ?? null,
          userId: updateRule.userId ?? null,
          groupId: updateRule.groupId ?? null,
          groupName: updateRule.groupName ?? null,
          assignScope,
          areaCodes: updateRule.areaCodes || [],
          active: updateRule.active ?? true,
          allAreaCodes: updateRule.allAreaCodes ?? false,
        }]);
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
        toast.error(translations[langMode].failedtoloadruledata);
      } finally {
        setLoadingAgents(false);
        setLoadingGeoCodes(false);
      }
    };
    fetchInitialData();
  }, [updateRule, projectId, langMode]);

  const handleChange = useCallback((index, field, selectedOptions, actionMeta) => {
    setRules((prevRules) => {
      const updatedRules = [...prevRules];
      const ruleToUpdate = { ...updatedRules[index] };

      if (field === "userId" || field === "workflowId") {
        ruleToUpdate[field] = selectedOptions ? selectedOptions.value : null;
      } else if (field === "groupId") {
        ruleToUpdate.groupId = selectedOptions ? selectedOptions.value : null;
        ruleToUpdate.groupName = selectedOptions ? selectedOptions.label : null;
      } else {
        const picked = selectedOptions || [];
        const hasAll = picked.some(opt => opt.value === "ALL");
        const wasAll = field === "areaCodes" && Boolean(ruleToUpdate.allAreaCodes);
        if (field === "areaCodes" && wasAll && hasAll) {
          // "All" is on, so every code shows ticked; unticking one code turns
          // "All" off and keeps the rest of the codes selected.
          const untickedCode = actionMeta?.option?.value ?? actionMeta?.removedValue?.value;
          if (untickedCode !== undefined && untickedCode !== "ALL") {
            ruleToUpdate.areaCodes = (Array.isArray(GeoCodes) ? GeoCodes : []).filter(
              (code) => code && code !== untickedCode
            );
            ruleToUpdate.allAreaCodes = false;
          }
        } else {
          const pickedCodes = picked.filter(opt => opt.value !== "ALL").map(item => item.value);
          // Ticking every code by hand is the same as picking "All".
          const availableCodes = (Array.isArray(GeoCodes) ? GeoCodes : []).filter(Boolean);
          const pickedEveryCode =
            field === "areaCodes" &&
            availableCodes.length > 0 &&
            availableCodes.every((code) => pickedCodes.includes(code));
          const isAllSelected = (hasAll && !wasAll) || pickedEveryCode;
          ruleToUpdate[field] = isAllSelected ? [] : pickedCodes;

          if (field === "areaCodes") {
            ruleToUpdate.allAreaCodes = isAllSelected;
          }
        }
      }
      updatedRules[index] = ruleToUpdate;
      return updatedRules;
    });
  }, [GeoCodes]);

  const setRuleAssignScope = (index, scope) => {
    setRules((prevRules) => {
      const updatedRules = [...prevRules];
      const current = { ...updatedRules[index], assignScope: scope };
      if (scope === "groups") {
        current.userId = null;
      } else {
        current.groupId = null;
        current.groupName = null;
      }
      updatedRules[index] = current;
      return updatedRules;
    });
  };

  const handleClose = () => {
    setUpdateOpen(false);
    setRules([emptySarRule()]);
  };

  const handleSubmit = async () => {
    const ruleToUpdate = rules[0];
    const isGroupScope = canUseGroups && getRuleAssignScope(ruleToUpdate) === "groups";
    if (isGroupScope && !ruleToUpdate.groupId) {
      toast.error(translations[langMode].selectGroup || "Select Group");
      return;
    }
    if (!isGroupScope && !ruleToUpdate.userId) {
      toast.error(translations[langMode].plzselectfieldagent);
      return;
    }

    // workflowId is optional on update -- omitting it keeps the rule's
    // current workflow (including legacy "All workflows" rules).
    const payload = {
      rule: {
        id: updateRule.id,
        ...(ruleToUpdate.workflowId ? { workflowId: ruleToUpdate.workflowId } : {}),
        userId: isGroupScope ? null : ruleToUpdate.userId,
        groupId: isGroupScope ? ruleToUpdate.groupId : null,
        areaCodes: ruleToUpdate.areaCodes,
        active: ruleToUpdate.active,
        allAreaCodes: ruleToUpdate.allAreaCodes,
      },
      projectId,
    };

    try {
      const result = await UpdateAssignrule(payload);
      if (result?.header?.code === 600) {
        toast.success(translations[langMode].ruleupdatedsuc);
        handleClose();
        onSaved?.();
      } else {
        toast.error(result?.body?.value || translations[langMode].failupdaterule);
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(getBackendMessage(error) || translations[langMode].failupdaterule);
    }
  };

  return (
    <Transition appear show={updateOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0 scale-95">
            <Dialog.Panel
              className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl transform transition-all flex flex-col overflow-hidden"
              style={{ maxHeight: "85vh" }}
            >
              {/* Header and footer are true shrink-0 flex siblings, same as
                  NewRuleModal.js/CompletionRuleModal.js/AssignmentRuleModal.js --
                  only the rule content in between scrolls. */}
              <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100">
                <Dialog.Title className="text-base font-semibold text-gray-800 truncate">
                  {translations[langMode].updateRule}
                </Dialog.Title>
                <ModalCloseButton onClick={handleClose} />
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5">
                {Array.isArray(rules) && rules.map((rule, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 p-4 sm:p-5 space-y-4"
                  >
                    <div className="flex flex-col gap-1.5 w-full">
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                        {translations[langMode].sarWorkflow || "Workflow"}
                      </label>
                      <ReactSelector
                        options={workflowOptions}
                        value={
                          rule.workflowId
                            ? workflowOptions.find((o) => o.value === rule.workflowId)
                              || { value: rule.workflowId, label: updateRule?.workflowName || `#${rule.workflowId}` }
                            : null
                        }
                        onChange={(selected) => handleChange(index, "workflowId", selected)}
                        components={{ Placeholder }}
                        placeholder={translations[langMode].allWorkflows || "All workflows"}
                        isSearchable
                        isClearable={false}
                        height="46px"
                        menuPortalTarget={document.body}
                        overflow="overlay"
                      />
                      {rule.workflowId && rule.workflowId !== (updateRule?.workflowId ?? null) && (
                        <p className="text-xs text-amber-600">
                          {translations[langMode].sarWorkflowChangeHint}
                        </p>
                      )}
                    </div>
                    <AssigneePicker
                      scope={getRuleAssignScope(rule)}
                      onScopeChange={(scope) => setRuleAssignScope(index, scope)}
                      langMode={langMode}
                      fieldAgent={agentList}
                      groupOptions={groupOptions}
                      userValue={
                        rule.userId
                          ? {
                            value: rule.userId,
                            label: agentList.find((agent) => agent.userId === rule.userId)?.userName,
                          }
                          : null
                      }
                      onUserChange={(selected) => handleChange(index, "userId", selected)}
                      groupValue={rule.groupId}
                      groupLabel={rule.groupName}
                      onGroupChange={(selected) => handleChange(index, "groupId", selected)}
                      userPlaceholder={loadingAgents ? "Loading Agents..." : translations[langMode].selectFieldAget}
                    />
                    {/* Same label + full-width control shape AssigneePicker
                        renders above, so Geo Codes lines up with the Assign
                        To selector instead of sitting inset in its own card. */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                        {translations[langMode].geoCodes}
                      </label>
                      <ReactSelector
                        options={(() => {
                          const codeOptions = (Array.isArray(GeoCodes) ? GeoCodes : []).filter(Boolean).map(code => ({
                            value: code,
                            label: code,
                          }));
                          return codeOptions.length ? [{ value: "ALL", label: "All" }, ...codeOptions] : [];
                        })()}
                        noOptionsMessage={() => translations[langMode]?.nodatavailvle ?? "No data available"}
                        value={
                          rule.allAreaCodes
                            ? [{ value: "ALL", label: "All" }]
                            : (rule.areaCodes || []).map(code => ({ value: code, label: code }))
                        }
                        onChange={(selected, meta) => handleChange(index, "areaCodes", selected, meta)}
                        isOptionSelected={(option, selectValue) =>
                          Boolean(rule.allAreaCodes) ||
                          (selectValue || []).some((item) => item.value === option.value)
                        }
                        closeMenuOnSelectValue="ALL"
                        components={{ Placeholder }}
                        placeholder={loadingGeoCodes ? translations[langMode].LoadingGeo : "Select..."}
                        isLoading={loadingGeoCodes}
                        height="46px"
                        isMulti
                        menuPortalTarget={document.body}
                        isClearable={false}
                        overflow="overlay"
                        selectedValuesMaxHeight="64px"
                      />
                    </div>
                    <div className="flex gap-2 justify-start items-center">
                      <span className="text-sm font-semibold text-gray-900">
                        {translations[langMode].Status}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={rule.active}
                        onClick={() => {
                          setRules((prev) => {
                            const updated = [...prev];
                            updated[index] = { ...updated[index], active: !rule.active };
                            return updated;
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#236DB4] ${rule.active ? "" : "bg-gray-200"}`}
                        style={rule.active ? { background: "#188A4B" } : undefined}
                      >
                        {/* translate-x-* is a physical shift, so under dir="rtl"
                            the knob starts at the pill's right edge and gets
                            pushed further right -- out of the pill and over the
                            Status label. The rtl: variants mirror it. */}
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${rule.active ? "translate-x-6 rtl:-translate-x-6" : "translate-x-1 rtl:-translate-x-1"}`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="shrink-0 flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-gray-100">
                <CommonButton colorIntent="danger"
                  variant="danger"
                  onClick={handleClose}
                >
                  {translations[langMode].cancle}
                </CommonButton>
                <CommonButton colorIntent="success"
                  onClick={handleSubmit}
                  variant="primary">
                  {translations[langMode].Update}
                </CommonButton>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
